/**
 * ECO.18 (26/09/2026) — ponte do term-deck web com o app Android (`apps/android`, plugin nativo `TermDeckApp`).
 *
 * No navegador o plugin não existe e tudo aqui responde "sem app": os consumidores seguem no caminho web de sempre.
 * No app, o que precisa sobreviver à tela bloqueada, ao app minimizado ou fechado vive no NATIVO (gravação em serviço
 * de primeiro plano, conteúdo compartilhado) e o web só dispara, acompanha e lê de volta — o WebView tem o áudio
 * mutado e o JS congelado em segundo plano, então nada disso pode depender dele.
 *
 * Contrato do lado nativo: `apps/android/android/app/src/main/java/tech/donizeti/termdeck/TermDeckAppPlugin.java`.
 */

export interface NativeRecordingState {
  recipient?: 'manager' | 'coordinator';
  recipientRouting?: boolean;
  active: boolean;
  id: string | null;
  key: string | null;
  startedAt: number;
  /** Pico desde a última leitura, 0..32767 (MediaRecorder.getMaxAmplitude). */
  amplitude: number;
  error: string | null;
  paused?: boolean;
  elapsedMs?: number;
  previewText?: string;
}

export interface NativeRecordingMeta {
  ownerRecipient?: 'manager' | 'coordinator';
  id: string;
  key: string;
  startedAt: number;
  stoppedAt?: number;
  status: 'recording' | 'stopped' | 'interrupted';
  size?: number;
  mimeType?: string;
  ownerQueued?: boolean;
  /** Texto do campo que segue com o áudio (#343); `ownerTextDropped`: o servidor recusou e o áudio foi sozinho. */
  ownerText?: string;
  ownerTextDropped?: boolean;
  serverStatus?: string;
  serverReason?: string;
  uploadedBytes?: number;
  durationMs?: number;
  ownerUploadId?: string;
  messageId?: string;
  previewText?: string;
}

export interface NativeSharedFile {
  id: string;
  name: string;
  mimeType: string;
  size: number;
}

export interface NativeSharedContent {
  present: boolean;
  text?: string;
  subject?: string;
  files?: NativeSharedFile[];
  /** Arquivos que o Android não deixou ler (permissão do app de origem, tamanho > 25 MB). */
  failedFiles?: number;
  receivedAt?: number;
}

/** Evento de diagnóstico do app (formato do `POST /api/app/android/diagnostics`). */
export interface NativeDiagEvent {
  id: string;
  atIso: string;
  source: 'app';
  kind: string;
  appVersion?: string;
  device?: Record<string, unknown>;
  detail?: string;
  meta?: Record<string, string | number | boolean>;
}

export interface NativeAppInfo {
  versionCode: number;
  versionName: string;
  platform: 'android';
  sdk: number;
  /** A partir do app 1.0.6. */
  androidRelease?: string;
  model?: string;
  canInstallUpdates: boolean;
  batteryExempt: boolean;
}

interface ListenerHandle { remove: () => Promise<void> | void }

export interface TermDeckAppPlugin {
  startOwnerNotifications?(opts: { server: string; after: number }): Promise<{ enabled: boolean }>;
  ownerNotificationStatus?(): Promise<{ state: string; allowed: boolean; muted?: boolean; batteryExempt?: boolean; backgroundRestricted?: boolean; miui?: boolean; locked?: boolean }>;
  /** Gesto do dono na conversa com o urgente à vista: confirma até esta sequência e para o alarme, mesmo sem rede (#345). */
  ackOwnerUrgent?(opts: { upTo: number }): Promise<{ sent: boolean }>;
  /** Ajustes que só o dono toca para receber com o app fechado (#345): bateria sem restrição e Início automático (MIUI). */
  openOwnerBackgroundSettings?(opts: { kind: 'battery' | 'autostart' }): Promise<{ opened: boolean }>;
  /** "Testar alertas": vibração direta, aviso normal ou urgente; devolve o retrato do que decide som e vibração no aparelho. */
  ownerAlertSelfTest?(opts: { kind: 'direct' | 'normal' | 'urgent'; delayMs?: number }): Promise<Record<string, unknown>>;
  setOwnerAlertsMuted?(opts: { muted: boolean }): Promise<{ state: string; allowed: boolean; muted?: boolean }>;
  openOwnerNotificationSettings?(): Promise<void>;
  /** Textos que seguiram com áudios encerrados pela notificação, por gravação; `ack` remove os já tratados pela tela (#343). */
  ownerTextConsumed?(opts?: { ack?: string[] }): Promise<{ items: Array<{ id: string; text: string; at: number }> }>;
  /** Guarda o campo na gravação ativa, para o encerrar pela notificação levar o texto junto (#343). */
  setOwnerRecordingText?(opts: { id: string; text: string }): Promise<{ saved: boolean }>;
  queueOwnerAudio?(opts:{ id: string; recipient?: 'manager' | 'coordinator'; deviceId: string; server: string; after: number; replyToMessageId?: string | undefined; text?: string | undefined }): Promise<{ enabled: boolean; textAttached?: boolean }>;
  info(): Promise<NativeAppInfo>;
  startRecording(opts: { key: string; recipient?: 'manager' | 'coordinator'; deviceId?: string; server?: string; after?: number; replyToMessageId?: string; text?: string }): Promise<NativeRecordingState>;
  pauseRecording?(opts?: { id: string }): Promise<NativeRecordingState>;
  resumeRecording?(opts?: { id: string }): Promise<NativeRecordingState>;
  cancelRecording?(opts?: { id: string }): Promise<NativeRecordingState>;
  /** `text`: o que está no campo vai na mesma mensagem do áudio (#343); `textAttached` confirma que foi gravado junto. */
  stopRecording(opts?: { id?: string; key?: string; text?: string }): Promise<{ id: string | null; size?: number; mimeType?: string; startedAt?: number; stoppedAt?: number; textAttached?: boolean }>;
  recordingStatus(): Promise<NativeRecordingState>;
  listRecordings(): Promise<{ recordings: NativeRecordingMeta[] }>;
  finishRecording(opts: { id: string }): Promise<void>;
  readFile(opts: { scope: 'recording' | 'shared'; id: string; offset: number; length?: number }): Promise<{ data: string; size: number; eof: boolean }>;
  takeSharedContent(): Promise<NativeSharedContent>;
  discardShared(opts: { id: string }): Promise<void>;
  requestBatteryExemption(): Promise<{ exempt: boolean; error?: string }>;
  installUpdate(opts: { url: string; sha256?: string | undefined }): Promise<{ started: boolean }>;
  /** ECO.24 — fila de autodiagnóstico gravada pelo nativo (sobrevive a travamento e a falta de rede). */
  pendingDiagnostics?(): Promise<{ events: NativeDiagEvent[]; device: Record<string, unknown> }>;
  acknowledgeDiagnostics?(opts: { ids: string[] }): Promise<{ removed: number }>;
  recordDiagnostic?(opts: { kind: string; detail?: string; where?: string }): Promise<void>;
  addListener(event: 'recordingState' | 'appResumed', cb: (state: NativeRecordingState) => void): Promise<ListenerHandle> | ListenerHandle;
  addListener(event: 'sharedContent', cb: () => void): Promise<ListenerHandle> | ListenerHandle;
  addListener(event: 'updateProgress', cb: (p: { done: number; total: number }) => void): Promise<ListenerHandle> | ListenerHandle;
}

interface CapacitorWindow {
  Capacitor?: {
    isNativePlatform?: () => boolean;
    Plugins?: { TermDeckApp?: TermDeckAppPlugin };
  };
}

/** O plugin do app, ou null no navegador. Lido a cada chamada (o bridge é injetado antes do bundle). */
export function nativeApp(win: unknown = typeof window !== 'undefined' ? window : undefined): TermDeckAppPlugin | null {
  try {
    const cap = (win as CapacitorWindow | undefined)?.Capacitor;
    if (!cap?.isNativePlatform?.()) return null;
    return cap.Plugins?.TermDeckApp ?? null;
  } catch {
    return null;
  }
}

export function isNativeApp(win?: unknown): boolean {
  return nativeApp(win) !== null;
}

function base64ToBytes(data: string): Uint8Array {
  const bin = atob(data);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/** Lê um arquivo do aparelho em pedaços (a ponte do Capacitor é JSON: pedaço grande único trava o WebView). */
export async function readNativeFile(
  plugin: TermDeckAppPlugin,
  scope: 'recording' | 'shared',
  id: string,
  mimeType: string,
): Promise<Blob> {
  const parts: Uint8Array[] = [];
  let offset = 0;
  for (let guard = 0; guard < 10_000; guard++) {
    const chunk = await plugin.readFile({ scope, id, offset, length: 512 * 1024 });
    if (chunk.data) {
      const bytes = base64ToBytes(chunk.data);
      parts.push(bytes);
      offset += bytes.length;
    }
    if (chunk.eof || !chunk.data) break;
    // Guarda: o nativo não pode fazer o laço passar do tamanho declarado (um offset ignorado repetiria o 1º pedaço).
    if (offset >= chunk.size) break;
  }
  if (offset !== parts.reduce((n, p) => n + p.length, 0)) throw new Error('leitura do arquivo do aparelho inconsistente');
  return new Blob(parts as BlobPart[], { type: mimeType });
}

/** Pico do MediaRecorder (0..32767) → nível 0..1 do medidor, em dB como o medidor do navegador. */
export function amplitudeToLevel(amplitude: number): number {
  if (!Number.isFinite(amplitude) || amplitude <= 0) return 0;
  const db = 20 * Math.log10(Math.min(1, amplitude / 32767));
  const level = (db + 50) / 42;
  return Math.max(0, Math.min(1, level));
}

// ── compartilhar para o term-deck ─────────────────────────────────────────────────────────────────────────────

const SHARED_STORAGE_KEY = 'td_native_shared_inbox';

/** Guarda o que chegou por "Compartilhar → term-deck" até o compositor do Assistente consumir. */
export function stashSharedContent(content: NativeSharedContent): void {
  try { sessionStorage.setItem(SHARED_STORAGE_KEY, JSON.stringify(content)); } catch { /* segue em memória */ }
  pendingShared = content;
  for (const listener of sharedListeners) listener();
}

let pendingShared: NativeSharedContent | null = null;
const sharedListeners = new Set<() => void>();

export function takeStashedShared(): NativeSharedContent | null {
  let content = pendingShared;
  if (!content) {
    try {
      const raw = sessionStorage.getItem(SHARED_STORAGE_KEY);
      content = raw ? (JSON.parse(raw) as NativeSharedContent) : null;
    } catch { content = null; }
  }
  pendingShared = null;
  try { sessionStorage.removeItem(SHARED_STORAGE_KEY); } catch { /* nada guardado */ }
  return content?.present ? content : null;
}

export function onStashedShared(listener: () => void): () => void {
  sharedListeners.add(listener);
  return () => { sharedListeners.delete(listener); };
}
